/**
 * Field — Form field wrapper (Design System §5)
 *
 * Label uses overline token.
 * Errors use money-out color with warning icon.
 * Passes through children as the control.
 */

import React, { useId } from 'react';

export interface FieldProps {
  label: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  children: (inputProps: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean; 'aria-required'?: boolean }) => React.ReactNode;
  className?: string;
}

export function Field({
  label,
  helperText,
  error,
  required = false,
  children,
  className,
}: FieldProps) {
  const id = useId();
  const descId = helperText || error ? `${id}-desc` : undefined;

  return (
    <div
      className={['ui-field', error ? 'ui-field--error' : '', className ?? ''].filter(Boolean).join(' ')}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
    >
      {/* Label */}
      <label
        htmlFor={id}
        className="ui-field__label"
        style={{
          fontSize: 'var(--text-overline-size)',
          lineHeight: 'var(--text-overline-lh)',
          fontWeight: 'var(--text-overline-weight)',
          letterSpacing: 'var(--text-overline-letter-spacing)',
          textTransform: 'uppercase',
          color: 'var(--color-text-secondary)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
        }}
      >
        {label}
        {required && (
          <span className="ui-field__required" aria-hidden="true" style={{ color: 'var(--color-validation-error)' }}>*</span>
        )}
      </label>

      {/* Control (injected via render prop) */}
      {children({
        id,
        'aria-describedby': descId,
        'aria-invalid': !!error,
        'aria-required': required,
      })}

      {/* Helper / error text */}
      {(helperText || error) && (
        <span
          id={descId}
          className={`ui-field__description${error ? ' ui-field__description--error' : ''}`}
          role={error ? 'alert' : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--text-caption-size)',
            lineHeight: 'var(--text-caption-lh)',
            color: error ? 'var(--color-validation-error)' : 'var(--color-text-secondary)',
          }}
        >
          {error && <span className="ui-field__error-icon" aria-hidden="true">⚠</span>}
          {error ?? helperText}
        </span>
      )}
    </div>
  );
}

/** Convenience: a plain text input styled for Ledger Mono */
export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean;
}

export function TextInput({ hasError, style, className, ...props }: TextInputProps) {
  const invalid = hasError || props['aria-invalid'] === true || props['aria-invalid'] === 'true';

  return (
    <input
      className={['ui-input', 'ui-text-input', invalid ? 'ui-text-input--error' : '', className ?? ''].filter(Boolean).join(' ')}
      data-error={invalid ? 'true' : undefined}
      style={{
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--text-body-size)',
        lineHeight: 'var(--text-body-lh)',
        fontWeight: 'var(--text-body-weight)',
        color: 'var(--color-text)',
        backgroundColor: 'var(--color-surface-subtle)',
        border: invalid ? 'var(--border-width-default) solid var(--color-validation-error)' : 'var(--border-width-default) solid var(--color-border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-2) var(--space-3)',
        width: '100%',
        transition: `border-color var(--motion-duration-press) var(--motion-easing)`,
        ...style,
      }}
      {...props}
    />
  );
}
